import React, { useState, useEffect, useCallback } from 'react';
import { enhancedAPI } from '../../api';
import { useUser } from '../../contexts/UserContext';
import TenantTable from './TenantTable';
import TenantForm from '../TenantForm';
import TenantDetailsDialog from './TenantDetailsDialog';
import {
  Button,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Add as AddIcon, People as PeopleIcon } from '@mui/icons-material';
import { RESIDENT } from '../../config/labels';

// Rent status filter options
const RENT_STATUS_OPTIONS = [
  { value: 'all',     label: 'All Rent Statuses' },
  { value: 'OVERDUE', label: '🔴 Overdue' },
  { value: 'PARTIAL', label: '🟡 Partial' },
  { value: 'PENDING', label: '⏳ Pending' },
  { value: 'PAID',    label: '✅ Paid' },
  { value: 'UNKNOWN', label: '— No Rent Set' },
];

/**
 * Active Tenants Section
 * Displays currently active tenants with full CRUD operations
 * 
 * Key behaviors:
 * - Loads immediately on page mount
 * - Shows only ACTIVE status tenants
 * - Respects existing filters: Branch, Room, Search
 * - Fully editable and actionable (edit, checkout, delete, payments)
 */
function ActiveTenantsSection() {
  const [tenants, setTenants] = useState([]);
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [tenantFormOpen, setTenantFormOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [checkoutDialog, setCheckoutDialog] = useState(false);
  const [checkoutTenant, setCheckoutTenant] = useState(null);
  const [vacatingDate, setVacatingDate] = useState('');
  const [rentStatusFilter, setRentStatusFilter] = useState('all');

  const { hasAnyRole } = useUser();

  // Fetch branches on component mount
  useEffect(() => {
    fetchBranches();
  }, []);

  // Fetch rooms when branch selection changes; reset room filter
  useEffect(() => {
    setSelectedRoom('all');
    if (selectedBranch !== 'all') {
      fetchRooms(selectedBranch);
    } else {
      setRooms([]);
    }
  }, [selectedBranch]);

  // Fetch active tenants when filters change
  useEffect(() => {
    if (branches.length > 0) {
      fetchActiveTenants();
    }
  }, [selectedBranch, selectedRoom, searchTerm, branches]);

  const fetchBranches = async () => {
    try {
      const response = await enhancedAPI.branches.list();
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
      showAlert('Failed to load branches', 'error');
    }
  };

  const fetchRooms = async (branchId) => {
    try {
      const response = await enhancedAPI.rooms.list({ branch: branchId });
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      showAlert('Failed to load rooms', 'error');
    }
  };

  /**
   * Fetch active tenants from backend
   * Respects existing filters: branch, room, search
   * Backend filters by status=active to exclude vacated tenants
   */
  const fetchActiveTenants = async () => {
    try {
      setLoading(true);
      
      // Backend filters active tenants (status=ACTIVE, vacating_date=null)
      const params = { status: 'active' };
      
      if (selectedBranch !== 'all') {
        params.branch = selectedBranch;
      }
      if (selectedRoom !== 'all') {
        params.room = selectedRoom;
      }
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      const response = await enhancedAPI.tenants.list(params);
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching active tenants:', error);
      showAlert('Failed to load active tenants', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
  };

  const handleAdd = () => {
    setEditingTenant(null);
    setTenantFormOpen(true);
  };

  const handleEdit = async (tenant) => {
    try {
      const response = await enhancedAPI.tenants.get(tenant.id);
      setEditingTenant(response.data);
      setTenantFormOpen(true);
    } catch (error) {
      console.error('Error fetching tenant details:', error);
      showAlert('Failed to load tenant details', 'error');
    }
  };

  const handleView = async (tenant) => {
    try {
      const response = await enhancedAPI.tenants.get(tenant.id);
      setSelectedTenant(response.data);
      setDetailsDialog(true);
    } catch (error) {
      console.error('Error fetching tenant details:', error);
      showAlert('Failed to load tenant details', 'error');
    }
  };

  // Extract the most meaningful message from an Axios error response
  const extractApiError = (error, fallback) => {
    const data = error?.response?.data;
    if (!data) return fallback;
    // Backend returns { error: '...', detail: '...' } or DRF default strings
    return data.error || data.detail || data.non_field_errors?.[0] || fallback;
  };

  const handleDelete = async (tenant) => {
    if (window.confirm(
      `Are you sure you want to permanently delete "${tenant.name}"?\n\nConsider using Checkout instead to preserve history.`
    )) {
      try {
        await enhancedAPI.tenants.delete(tenant.id);
        showAlert('Tenant deleted successfully');
        fetchActiveTenants();
      } catch (error) {
        console.error('Delete tenant error:', {
          status: error?.response?.status,
          data: error?.response?.data,
          url: error?.config?.url,
        });
        showAlert(extractApiError(error, 'Failed to delete tenant'), 'error');
      }
    }
  };

  const handleCheckout = (tenant) => {
    setCheckoutTenant(tenant);
    const today = new Date().toISOString().split('T')[0];
    setVacatingDate(today);
    setCheckoutDialog(true);
  };

  const handleCheckoutConfirm = async () => {
    if (!checkoutTenant || !vacatingDate) {
      showAlert('Please select a vacating date', 'error');
      return;
    }

    try {
      await enhancedAPI.tenants.checkout(checkoutTenant.id, { vacating_date: vacatingDate });
      showAlert('Tenant checked out successfully');
      setCheckoutDialog(false);
      setCheckoutTenant(null);
      setVacatingDate('');
      fetchActiveTenants();
    } catch (error) {
      console.error('Checkout tenant error:', {
        tenantId: checkoutTenant?.id,
        status: error?.response?.status,
        data: error?.response?.data,
        url: error?.config?.url,
      });
      showAlert(extractApiError(error, 'Failed to checkout tenant'), 'error');
    }
  };

  const handleFormClose = () => {
    setTenantFormOpen(false);
    setEditingTenant(null);
  };

  const handleFormSave = () => {
    fetchActiveTenants();
    setTenantFormOpen(false);
    setEditingTenant(null);
    showAlert(editingTenant ? `${RESIDENT.singular} updated successfully` : `${RESIDENT.singular} added successfully`);
  };

  const canEdit = hasAnyRole(['owner', 'admin', 'warden']);
  const canDelete = hasAnyRole(['owner', 'admin']);
  const canAdd = hasAnyRole(['owner', 'admin', 'warden']);

  // ── Rent status summary counts (computed from fetched data) ──────────────
  const rentSummary = tenants.reduce(
    (acc, t) => {
      const rs = t.current_rent_status?.rent_status || 'UNKNOWN';
      acc[rs] = (acc[rs] || 0) + 1;
      return acc;
    },
    {}
  );

  // ── Client-side rent status filter ──────────────────────────────────────
  const displayedTenants = rentStatusFilter === 'all'
    ? tenants
    : tenants.filter(t => (t.current_rent_status?.rent_status || 'UNKNOWN') === rentStatusFilter);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      {/* Section Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <PeopleIcon color="primary" fontSize="large" />
        <Typography variant="h5" component="h2">
          {RESIDENT.activeSection}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          ({loading ? '...' : displayedTenants.length}{rentStatusFilter !== 'all' ? ` of ${tenants.length}` : ''})
        </Typography>
      </Box>

      {/* ── Rent Status Summary Bar ─────────────────────────────────────── */}
      {!loading && tenants.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
            Rent Summary:
          </Typography>
          {[
            { key: 'OVERDUE',  label: 'Overdue',  color: 'error',   icon: '🔴' },
            { key: 'PARTIAL',  label: 'Partial',  color: 'warning', icon: '🟡' },
            { key: 'PENDING',  label: 'Pending',  color: 'default', icon: '⏳' },
            { key: 'PAID',     label: 'Paid',     color: 'success', icon: '✅' },
            { key: 'UNKNOWN',  label: 'No Rent Set', color: 'default', icon: '—' },
          ].filter(s => rentSummary[s.key] > 0).map(({ key, label, color, icon }) => (
            <Alert
              key={key}
              severity={color === 'error' ? 'error' : color === 'warning' ? 'warning' : color === 'success' ? 'success' : 'info'}
              icon={false}
              onClick={() => setRentStatusFilter(rentStatusFilter === key ? 'all' : key)}
              sx={{
                py: 0.25,
                px: 1.25,
                cursor: 'pointer',
                border: rentStatusFilter === key ? '2px solid currentColor' : '1px solid transparent',
                borderRadius: 2,
                fontWeight: rentStatusFilter === key ? 700 : 400,
                '& .MuiAlert-message': { fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 0.5 },
              }}
            >
              {icon} {rentSummary[key]} {label}
            </Alert>
          ))}
          {rentStatusFilter !== 'all' && (
            <Typography
              variant="caption"
              sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline', ml: 0.5 }}
              onClick={() => setRentStatusFilter('all')}
            >
              Clear filter
            </Typography>
          )}
        </Box>
      )}

      {/* Filters and Actions - EXISTING filters preserved exactly */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          label={`Search ${RESIDENT.plural.toLowerCase()}...`}
          placeholder="Name, phone, email, room..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ flexGrow: 1, minWidth: '200px' }}
        />
        
        <FormControl size="small" sx={{ minWidth: '150px' }}>
          <InputLabel>Branch</InputLabel>
          <Select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            label="Branch"
          >
            <MenuItem value="all">All Branches</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                {branch.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedBranch !== 'all' && (
          <FormControl size="small" sx={{ minWidth: '150px' }}>
            <InputLabel>Room</InputLabel>
            <Select
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
              label="Room"
            >
              <MenuItem value="all">All Rooms</MenuItem>
              {rooms.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  {room.room_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: '170px' }}>
          <InputLabel>Rent Status</InputLabel>
          <Select
            value={rentStatusFilter}
            onChange={(e) => setRentStatusFilter(e.target.value)}
            label="Rent Status"
          >
            {RENT_STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {canAdd && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            {RESIDENT.add}
          </Button>
        )}
      </Box>

      {/* Tenant Table - reusing existing component */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TenantTable
          tenants={displayedTenants}
          loading={loading}
          readOnly={false}
          showVacatedDate={false}
          showRentStatus={true}
          canEdit={canEdit}
          canDelete={canDelete}
          onView={handleView}
          onEdit={handleEdit}
          onCheckout={handleCheckout}
          onDelete={handleDelete}
        />
      )}

      {/* Tenant Form Dialog - EXISTING functionality preserved */}
      {tenantFormOpen && (
        <TenantForm
          open={tenantFormOpen}
          onClose={handleFormClose}
          onSave={handleFormSave}
          tenant={editingTenant}
          isEdit={!!editingTenant}
        />
      )}

      {/* Tenant Details Dialog */}
      {detailsDialog && (
        <TenantDetailsDialog
          open={detailsDialog}
          onClose={() => setDetailsDialog(false)}
          tenant={selectedTenant}
          onPaymentRecorded={fetchActiveTenants}
        />
      )}

      {/* Checkout Confirmation Dialog */}
      <Dialog open={checkoutDialog} onClose={() => setCheckoutDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Checkout {RESIDENT.singular}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Checking out: <strong>{checkoutTenant?.name}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Vacating Date"
            type="date"
            value={vacatingDate}
            onChange={(e) => setVacatingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Select the date when the tenant vacated"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckoutDialog(false)}>Cancel</Button>
          <Button onClick={handleCheckoutConfirm} variant="contained" color="warning">
            Confirm Checkout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar - EXISTING notification system preserved */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default ActiveTenantsSection;
